import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import DemoViewSwitcher from "./components/DemoViewSwitcher";
import GatewayDemoSwitcher from "./components/demo/GatewayDemoSwitcher";
import { tokenStore } from "./lib/api";
import "@/App.css";

// Everything except Landing is lazy-loaded — before this, every route's
// code (all 8 admin pages included) shipped in one ~1.4MB bundle that a
// brand-new visitor had to download and parse in full just to see the
// landing page, even though they'd only ever need Landing's own code that
// visit. Landing stays a normal top-level import since it's the one page
// almost everyone actually lands on and it must be there the instant the
// bundle runs — everything else only needs to exist once someone actually
// navigates to it.
const ExternalLogin = lazy(() => import("./pages/ExternalLogin"));
const SsoCallback = lazy(() => import("./pages/SsoCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PoemPlayer = lazy(() => import("./pages/PoemPlayer"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Record = lazy(() => import("./pages/Record"));
const AdminPoems = lazy(() => import("./pages/AdminPoems"));
const AdminPoemEditor = lazy(() => import("./pages/AdminPoemEditor"));
const AdminBulkImport = lazy(() => import("./pages/AdminBulkImport"));
const AdminInbox = lazy(() => import("./pages/AdminInbox"));
const AdminGuardians = lazy(() => import("./pages/AdminGuardians"));
const AdminSchools = lazy(() => import("./pages/AdminSchools"));
const AdminAuditLog = lazy(() => import("./pages/AdminAuditLog"));
const AdminQuizzes = lazy(() => import("./pages/AdminQuizzes"));
const AdminReleaseManager = lazy(() => import("./pages/AdminReleaseManager"));
const AdminLearnerOverrides = lazy(() => import("./pages/AdminLearnerOverrides"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminHowTo = lazy(() => import("./pages/AdminHowTo"));
const HowToUse = lazy(() => import("./pages/HowToUse"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const TeacherPrepare = lazy(() => import("./pages/TeacherPrepare"));
const TeacherProgress = lazy(() => import("./pages/TeacherProgress"));
const PrincipalDashboard = lazy(() => import("./pages/PrincipalDashboard"));
const PrincipalTeachers = lazy(() => import("./pages/PrincipalTeachers"));
const PrincipalStudents = lazy(() => import("./pages/PrincipalStudents"));
const SchoolNotices = lazy(() => import("./pages/SchoolNotices"));
const GatewaySchool = lazy(() => import("./pages/GatewaySchool"));
const AdminSchoolAnalytics = lazy(() => import("./pages/AdminSchoolAnalytics"));
const PrincipalProgress = lazy(() => import("./pages/PrincipalProgress"));
const Capstone = lazy(() => import("./pages/Capstone"));
const SchoolOnboardingForm = lazy(() => import("./pages/SchoolOnboardingForm"));
const QuranFlipbookPage = lazy(() => import("./pages/QuranFlipbookPage"));

// A blank cream screen for the brief moment a lazy route's own chunk is
// still downloading — matches the app's background so it reads as "still
// loading" rather than a flash of white.
function RouteFallback() {
    return <div className="min-h-screen bg-cream" />;
}

function AppRouter() {
    return (
        <>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<ExternalLogin />} />
                    <Route path="/register" element={<ExternalLogin mode="register" />} />
                    <Route path="/sso/callback" element={<SsoCallback />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    {/* The onboarding questions were removed: anyone landing here (old links, bookmarks) goes straight to the dashboard. */}
                    <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/poem/:poemId" element={<PoemPlayer />} />
                    <Route path="/poem/:poemId/quiz" element={<Quiz />} />
                    <Route path="/poem/:poemId/record" element={<Record />} />
                    <Route path="/admin" element={<AdminPoems />} />
                    <Route path="/admin/import" element={<AdminBulkImport />} />
                    <Route path="/admin/inbox" element={<AdminInbox />} />
                    <Route path="/admin/guardians" element={<AdminGuardians />} />
                    <Route path="/admin/schools" element={<AdminSchools />} />
                    <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                    <Route path="/admin/quizzes" element={<AdminQuizzes />} />
                    <Route path="/admin/release" element={<AdminReleaseManager />} />
                    <Route path="/admin/learner-overrides" element={<AdminLearnerOverrides />} />
                    <Route path="/admin/analytics" element={<AdminAnalytics />} />
                    <Route path="/admin/school-progress" element={<AdminSchoolAnalytics />} />
                    <Route path="/admin/how-to" element={<AdminHowTo />} />
                    <Route path="/how-to-use" element={<HowToUse />} />
                    <Route path="/admin/poems/:poemId" element={<AdminPoemEditor />} />
                    <Route path="/teacher" element={<TeacherDashboard />} />
                    <Route path="/teacher/prepare" element={<TeacherPrepare />} />
                    <Route path="/teacher/progress" element={<TeacherProgress />} />
                    <Route path="/principal" element={<PrincipalDashboard />} />
                    <Route path="/principal/teachers" element={<PrincipalTeachers />} />
                    <Route path="/principal/students" element={<PrincipalStudents />} />
                    <Route path="/principal/progress" element={<PrincipalProgress />} />
                    {/* One route for both roles — SchoolNotices picks the
                        principal or teacher shell itself, since the board
                        below it is identical for each. */}
                    <Route path="/school" element={<GatewaySchool />} />
                    <Route path="/school/notices" element={<SchoolNotices />} />
                    <Route path="/capstone" element={<Capstone />} />
                    {/* Public, no login — the link sent directly to a school so
                        their own staff can fill in the full roster (principal,
                        teachers, students) themselves. See AdminInbox.jsx's
                        "School Onboarding" tab for the admin side. */}
                    <Route path="/for-schools/onboarding" element={<SchoolOnboardingForm />} />
                    {/* Real routes for the 3D flipbooks — replaces the old
                        iframe-embedded static HTML (see QuranFlipbookPage.jsx for why).
                        Poem 4's own route restored 2026-08-18 alongside
                        REFERENCE_FLIPBOOK_POEMS in StoryBookViewer.jsx, now that its
                        assets/content are in place. */}
                    <Route path="/quran-explorer" element={<QuranFlipbookPage poemKey="poem_01" />} />
                    <Route path="/quran-explorer-poem02" element={<QuranFlipbookPage poemKey="poem_02" />} />
                    <Route path="/quran-explorer-poem03" element={<QuranFlipbookPage poemKey="poem_03" />} />
                    <Route path="/quran-explorer-poem04" element={<QuranFlipbookPage poemKey="poem_04" />} />
                    <Route path="/quran-explorer-poem05" element={<QuranFlipbookPage poemKey="poem_05" />} />
                    <Route path="/quran-explorer-poem06" element={<QuranFlipbookPage poemKey="poem_06" />} />
                    <Route path="/quran-explorer-poem07" element={<QuranFlipbookPage poemKey="poem_07" />} />
                    <Route path="/quran-explorer-poem08" element={<QuranFlipbookPage poemKey="poem_08" />} />
                    <Route path="/quran-explorer-poem09" element={<QuranFlipbookPage poemKey="poem_09" />} />
                    <Route path="/quran-explorer-poem10" element={<QuranFlipbookPage poemKey="poem_10" />} />
                    <Route path="/quran-explorer-poem11" element={<QuranFlipbookPage poemKey="poem_11" />} />
                    <Route path="/quran-explorer-poem12" element={<QuranFlipbookPage poemKey="poem_12" />} />
                    <Route path="/quran-explorer-poem13" element={<QuranFlipbookPage poemKey="poem_13" />} />
                    <Route path="/quran-explorer-poem14" element={<QuranFlipbookPage poemKey="poem_14" />} />
                    <Route path="/quran-explorer-poem15" element={<QuranFlipbookPage poemKey="poem_15" />} />
                    <Route path="/quran-explorer-poem16" element={<QuranFlipbookPage poemKey="poem_16" />} />
                    <Route path="/quran-explorer-poem17" element={<QuranFlipbookPage poemKey="poem_17" />} />
                    <Route path="/quran-explorer-poem18" element={<QuranFlipbookPage poemKey="poem_18" />} />
                    <Route path="/quran-explorer-poem19" element={<QuranFlipbookPage poemKey="poem_19" />} />
                    <Route path="/quran-explorer-poem20" element={<QuranFlipbookPage poemKey="poem_20" />} />
                </Routes>
            </Suspense>
            <DemoViewSwitcher />
            <GatewayDemoSwitcher gatewayUrl={process.env.REACT_APP_GATEWAY_URL} getToken={tokenStore.get} setToken={tokenStore.set} homes={{ default: "/dashboard" }} />
        </>
    );
}

function App() {
    return (
        <LanguageProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRouter />
                    <Toaster position="top-center" richColors />
                </BrowserRouter>
            </AuthProvider>
        </LanguageProvider>
    );
}

export default App;
